import type { CheckListItem } from '../types/types';
import { ACTIVE_FIND_POSITION } from './domConstants';
export const SHOPABLE_PREFIX = '@s@';
export const CONVERTABLE_PREFIX = '@c@';

export const PRIORITY = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

export const TITLE_IS_TOO_LONG: CheckListItem = {
  id: `${SHOPABLE_PREFIX}title-is-too-long`,
  key: 'TITLE_IS_TOO_LONG',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.HIGH,
  title: 'Lengthy headline',
  titleSuccess: 'Concised headline',
  descriptionSuccess: 'It encourages buyers to scan information on your page faster.',
  description: 'Shorten to under 70 characters to keep headlines concise and compelling.',
  propertyName: '',
};

export const CAN_NOT_SEND_EMAIL: CheckListItem = {
  id: `${SHOPABLE_PREFIX}can-not-send-email`,
  key: 'CAN_NOT_SEND_EMAIL',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.CRITICAL,
  title: 'Incorrect field name',
  titleSuccess: 'Correct field name',
  descriptionSuccess: 'Your form is ready to go!',
  description:
    'Ensure that all text field and text area are followed by <a class="text-blue-350" href="https://shopify.dev/themes/customer-engagement/add-contact-form#required-inputs" target="_blank">Shopify rules</a>, e.g. <strong>contact[name]</strong> or <strong>contact[phone]</strong> or <strong>contact[email]</strong>.',
  propertyName: 'name',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.PARENT,
};

export const HARD_TO_READ_CONTENT: CheckListItem = {
  id: `${SHOPABLE_PREFIX}hard-to-read-content`,
  key: 'HARD_TO_READ_CONTENT',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.HIGH,
  title: 'Hard to read text',
  titleSuccess: 'Readable text',
  descriptionSuccess: 'It helps visitors not miss out on important information.',
  description: `Ensure your font size should be at least 10px for Paragraph and 11px for Heading.`,
  propertyName: 'font-size',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.NEXT,
};

export const HARD_TO_READ_BUTTON: CheckListItem = {
  id: `${SHOPABLE_PREFIX}hard-to-read-button`,
  key: 'HARD_TO_READ_BUTTON',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.MEDIUM,
  title: 'Unnoticeable button',
  titleSuccess: 'Visible button',
  descriptionSuccess: 'Visitors can view the call-to-action clearly on any device.',
  description: 'Use bigger font-size or darker background to help the call-to-action stand out.',
  propertyName: 'button-color',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.PARENT,
};

export const HARD_TO_CLICK_BUTTON: CheckListItem = {
  id: `${SHOPABLE_PREFIX}hard-to-click-button`,
  key: 'HARD_TO_CLICK_BUTTON',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.HIGH,
  title: 'Hard to click button',
  titleSuccess: 'Accessible button',
  descriptionSuccess: "It's easier for visitors to interact with larger buttons.",
  description: "Small button is hard to click. Make sure your button's height should be at least 38 pixels.",
  propertyName: 'height',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.NEXT,
};

export const MISSING_LINK: CheckListItem = {
  id: `${SHOPABLE_PREFIX}missing-link`,
  key: 'MISSING_LINK',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.CRITICAL,
  title: 'Missing button link',
  titleSuccess: 'Functional button',
  descriptionSuccess: 'All the buttons work properly now.',
  description: 'Ensure to enter URL destination to avoid confusion and missing sales.',
  propertyName: 'editLink',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.PARENT,
};

export const NO_SOURCE_VIDEO_MEDIA: CheckListItem = {
  id: `${SHOPABLE_PREFIX}no-source-video`,
  key: 'NO_SOURCE_VIDEO',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.CRITICAL,
  title: 'Missing URL source video',
  titleSuccess: 'Fulfilled video',
  descriptionSuccess: 'Your video displays content correctly.',
  description: 'Include a URL source to show content properly.',
  propertyName: 'video_source',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.PARENT,
};

export const NO_SOURCE_VIDEO: CheckListItem = {
  id: `${SHOPABLE_PREFIX}no-source-video`,
  key: 'NO_SOURCE_VIDEO',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.CRITICAL,
  title: 'Missing URL source video',
  titleSuccess: 'Fulfilled video',
  descriptionSuccess: 'Your video displays content correctly.',
  description: 'Include a URL source to show content properly.',
  propertyName: ['editLink', 'url', 'vimeo-url', 'video_source', 'youtube-url', 'custom-url'],
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.PARENT,
};

export const WEAK_SENSE_OF_URGENCY: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}weak-sense-of-urgency`,
  key: 'WEAK_SENSE_OF_URGENCY',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.MEDIUM,
  title: 'Weak sense of urgency',
  titleSuccess: 'Strong sense of urgency',
  descriptionSuccess: 'Encourage buyers to make decision before running out of time.',
  description:
    'Shorten the time interval to increase the urge of buying decision. \n Our recommendation is to set up the time range below 48 hours.',
  propertyName: 'everGreen',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.PARENT,
};

export const WEAK_CALL_TO_ACTION: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}weak-call-to-action`,
  key: 'WEAK_CALL_TO_ACTION',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.MEDIUM,
  title: 'Lengthy CTA button',
  titleSuccess: 'Clear CTA button',
  descriptionSuccess:
    'Try to write your call-to-action more creative, not just "Buy Now", but can be "Yes, I want it!", "Lock The Deal",...',
  description: 'Keep your CTA under 24 characters to make it clear and bold.',
  propertyName: 'editText',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.PARENT,
};

export const NO_CALL_TO_ACTION_FOUND: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}no-call-to-action-found`,
  key: 'NO_CALL_TO_ACTION_FOUND',
  isPageRule: true,
  version: 'v1',
  priority: PRIORITY.HIGH,
  title: 'Missing CTA button',
  titleSuccess: 'Activated CTA button',
  descriptionSuccess: "Button is available now! It helps to turn visitors to customers and drive more sales.",
  description: 'Button helps to boost your page\'s conversion rate. Ensure to have at least 1 button in your page to turn visitors to customers.',
  propertyName: '',
};

export const WEAK_SENSE_OF_TRUST: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}weak-sense-of-trust`,
  key: 'WEAK_SENSE_OF_TRUST',
  isPageRule: true,
  version: 'v1',
  priority: PRIORITY.HIGH,
  title: 'Weak sense of trust',
  titleSuccess: 'Trusted page',
  descriptionSuccess: 'Your page looks trustworthy now! Positive reviews are powerful in creating trust and increasing sales significantly.',
  description: "Trust signal helps visitors feel more confident in their buying decisions. Don't forget to embed product review element to make your page more trustworthy.",
  propertyName: '',
};

export const WEAK_SEO: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}seo`,
  key: 'WEAK_SEO',
  isPageRule: true,
  version: 'v1',
  priority: PRIORITY.HIGH,
  title: 'Weak SEO',
  titleSuccess: 'Strong SEO',
  descriptionSuccess:
    'SEO title and description are included in your page now! It can optimize your page ranking on search engines and drive the maximum amount of organic traffic to it.',
  description:
    "SEO improves your page's rankings in search engine results. Ensure to fill out at least 30 to 60 characters for SEO Page Title and 50 to 150 for SEO Description to boost SEO ranking better.",
  propertyName: '',
};

export const MISSING_LAZY_LOADING: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}lazy-load`,
  key: 'MISSING_LAZY_LOADING',
  isPageRule: true,
  version: 'v1',
  priority: PRIORITY.HIGH,
  title: 'Missing lazy loading',
  titleSuccess: 'Enabled lazy loading',
  descriptionSuccess:
    'Lazy loading is enabled now! It reduces the loading time of your page and increases conversion rate.',
  description:
    "Lazy loading means preserving bandwidth and improving site performance. It signals the browser to load the content that is on display and delay the remaining until scrolled into view. Ensure to turn on Lazy Loading when publishing to reduce page's loading time and increase your conversion rate.",
  propertyName: '',
};

export const PAGE_HEIGHT_IS_TOO_LONG: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}page-height-is-too-long`,
  key: 'PAGE_HEIGHT_IS_TOO_LONG',
  isPageRule: true,
  version: 'v1',
  priority: PRIORITY.MEDIUM,
  title: 'Lengthy page',
  titleSuccess: 'Optimal page length',
  descriptionSuccess: 'Your visitors do not feel overwhelmed when scrolling the page on their phone.',
  description: 'Keep the mobile page height not greater than 3000px to engage with your visitors entirely.',
  devices: ['xs'],
  propertyName: '',
};

export const DIFFERENT_HEADING_FONT_FAMILY: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}different-heading-font-family`,
  key: 'DIFFERENT_HEADING_FONT_FAMILY',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.MEDIUM,
  title: 'Different heading font',
  titleSuccess: 'Consistent heading font',
  descriptionSuccess: 'Use the same font style to help user read information easily.',
  description: 'Use the same font style to help user read information easily.',
  propertyName: 'font',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.NEXT,
};

export const DIFFERENT_BUTTON_FONT_FAMILY: CheckListItem = {
  id: `${CONVERTABLE_PREFIX}different-button-font-family`,
  key: 'DIFFERENT_BUTTON_FONT_FAMILY',
  isPageRule: false,
  version: 'v1',
  priority: PRIORITY.MEDIUM,
  title: 'Different button font',
  titleSuccess: 'Consistent button font',
  descriptionSuccess: 'Use the same font style to help user read information easily.',
  description: 'Use the same font style to help user read information easily.',
  propertyName: 'font',
  activePositionByPropertyElement: ACTIVE_FIND_POSITION.NEXT,
};

const CHECK_LIST = {
  TITLE_IS_TOO_LONG,
  CAN_NOT_SEND_EMAIL,
  HARD_TO_READ_CONTENT,
  HARD_TO_READ_BUTTON,
  HARD_TO_CLICK_BUTTON,
  MISSING_LINK,
  WEAK_SENSE_OF_URGENCY,
  WEAK_CALL_TO_ACTION,
  NO_CALL_TO_ACTION_FOUND,
  WEAK_SENSE_OF_TRUST,
  PAGE_HEIGHT_IS_TOO_LONG,
  DIFFERENT_HEADING_FONT_FAMILY,
  DIFFERENT_BUTTON_FONT_FAMILY,
  NO_SOURCE_VIDEO,
  MISSING_LAZY_LOADING,
  WEAK_SEO,
};

export default CHECK_LIST;
